/**
 * Step Study Cohort detail: view stats, add weekly entries, graduate, drop trends.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { usePermissions } from '../hooks/usePermissions';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip } from 'chart.js';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { get, post, patch, del, getErrorMessage } from '../api/client';
import { CLASS_TYPES, MEETING_DAYS, MEETING_TIME_OPTIONS } from '../config/stepStudyConfig';
import { formatUsLocalPhone } from '../utils/phoneFormat';
import { attendanceRecordStatusFromApi, attendancePercentToneClass } from '../utils/stepStudyAttendance';
import './StepStudyDashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Latest meeting week first (by entryDate), regardless of save order; tie-break by createdAt. */
function sortWeekEntriesDesc(list) {
  if (!list?.length) return [];
  return [...list].sort((a, b) => {
    const tb = new Date(b.entryDate).getTime();
    const ta = new Date(a.entryDate).getTime();
    if (tb !== ta) return tb - ta;
    const cb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    const ca = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    if (cb !== ca) return cb - ca;
    return String(b._id || '').localeCompare(String(a._id || ''));
  });
}

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Shorten long fields so PDF/table rows stay a single line. */
function truncateOneLine(value, maxChars) {
  const s = String(value ?? '').trim();
  if (s.length <= maxChars) return s;
  return `${s.slice(0, Math.max(0, maxChars - 1))}…`;
}

/** Copy for roster print/PDF footer: next meeting date/time + sign-in wording. */
function buildRosterSignInFooter(data, displayDate, timeInfo) {
  const hasWhen = displayDate && displayDate !== '—';
  const dayPrefix = data.meetingDay ? `${data.meetingDay}, ` : '';
  if (hasWhen) {
    return `Expected next meeting: ${dayPrefix}${displayDate}${timeInfo || ''}. Bring this roster for students to sign in when they arrive.`;
  }
  return 'Bring this roster for students to sign in at the next meeting.';
}

/** Monday 00:00 local — ISO week start for bucketing an entry date to a class week. */
function startOfWeekMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

/** 0–6 Sun–Sat per `Date#getDay()`. Defaults to Monday if unset. */
function meetingDayIndexFromLabel(label) {
  if (!label || typeof label !== 'string') return 1;
  const i = MEETING_DAYS.indexOf(label.trim());
  return i >= 0 ? i : 1;
}

/** The cohort’s meeting on the given ISO week (`monday` = that week’s Monday at local midnight). */
function meetingDateForMondayWeek(monday, meetingDayIndex) {
  const m = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate());
  m.setHours(0, 0, 0, 0);
  const mondayDow = 1;
  const delta = (meetingDayIndex - mondayDow + 7) % 7;
  m.setDate(m.getDate() + delta);
  return m;
}

/** Normalize a saved weekly `entryDate` to the class meeting date for that week (e.g. Saturday). */
function canonicalMeetingDateForEntryDate(entryDate, meetingDayIndex) {
  return meetingDateForMondayWeek(startOfWeekMonday(entryDate), meetingDayIndex);
}

const StepStudyCohortPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAccessStepStudyCohort } = usePermissions();
  const [cohort, setCohort] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [entryEditForm, setEntryEditForm] = useState({});
  const [entryDate, setEntryDate] = useState('');
  const [activeStudents, setActiveStudents] = useState('');
  const [droppedThisWeek, setDroppedThisWeek] = useState('0');
  const [droppedAtLesson, setDroppedAtLesson] = useState('');
  const [newThisWeek, setNewThisWeek] = useState('0');
  const [lessonNumber, setLessonNumber] = useState('1');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropTrends, setDropTrends] = useState({ byLesson: [], totalDrops: 0 });
  const [cohortPageInstructionsOpen, setCohortPageInstructionsOpen] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberForm, setMemberForm] = useState({ firstName: '', lastName: '', cellphone: '', email: '', okTexts: true, okEmails: true, isLeader: false });
  /** Roster Attendance: which week is selected — saved entry, or a calendar week slot (creates row on Save). */
  const [attendanceSelection, setAttendanceSelection] = useState(null);
  const [attendanceLessonInput, setAttendanceLessonInput] = useState('1');
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [isSavingAttendance, setIsSavingAttendance] = useState(false);
  const [attendanceSaveError, setAttendanceSaveError] = useState('');
  const [sectionOpen, setSectionOpen] = useState({ roster: false, attendance: false, addEntry: false, weeklyHistory: false, activeChart: false, dropTrends: false, rosterStats: false });

  useEffect(() => {
    if (!id) return;
    if (!canAccessStepStudyCohort(id)) {
      navigate('/step-study', { replace: true });
      return;
    }
    Promise.all([
      get(`/api/step-study/cohorts/${id}`),
      get(`/api/step-study/cohorts/${id}/drop-trends`),
    ]).then(([cohortRes, trendsRes]) => {
      if (cohortRes.ok && cohortRes.data) {
        const data = cohortRes.data;
        setCohort(data);
        setEditForm({
          classType: data.classType,
          startDate: data.startDate ? new Date(data.startDate).toISOString().split('T')[0] : '',
          expectedGraduationDate: data.expectedGraduationDate ? new Date(data.expectedGraduationDate).toISOString().split('T')[0] : '',
          leader1: data.leader1 || '',
          leader2: data.leader2 || '',
          leader3: data.leader3 || '',
          meetingDay: data.meetingDay || '',
          meetingTimeStart: data.meetingTimeStart || '',
          meetingTimeEnd: data.meetingTimeEnd || '',
          useRoster: !!data.useRoster,
        });
        const sortedEntries = sortWeekEntriesDesc(data?.entries);
        const latest = sortedEntries[0];
        if (data?.useRoster) {
          const activeCount = (data.roster || []).filter((m) => m.status === 'active').length;
          setActiveStudents(String(activeCount));
        } else if (latest) {
          setActiveStudents(String(latest.activeStudents ?? ''));
        }
      }
      if (trendsRes.ok && trendsRes.data) {
        setDropTrends({ byLesson: trendsRes.data.byLesson || [], totalDrops: trendsRes.data.totalDrops || 0 });
      }
    });
  }, [id, canAccessStepStudyCohort, navigate]);

  useEffect(() => {
    if (!cohort?.useRoster) return;
    const n = (cohort.roster || []).filter((m) => m.status === 'active').length;
    setActiveStudents(String(n));
  }, [cohort?.useRoster, cohort?.roster]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!entryDate) {
      setError('Entry date required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        entryDate,
        activeStudents: Number(activeStudents) || 0,
        droppedThisWeek: Number(droppedThisWeek) || 0,
        newThisWeek: Number(newThisWeek) || 0,
        lessonNumber: Number(lessonNumber) || 1,
      };
      if (droppedAtLesson.trim()) payload.droppedAtLesson = Number(droppedAtLesson);
      const { ok, status, data: res } = await post(`/api/step-study/cohorts/${id}/weeks`, payload);
      if (!ok) {
        setError(getErrorMessage({ status, data: res }, 'Failed to add weekly entry.'));
        return;
      }
      const [cohortRes, trendsRes] = await Promise.all([
        get(`/api/step-study/cohorts/${id}`),
        get(`/api/step-study/cohorts/${id}/drop-trends`),
      ]);
      if (cohortRes.ok && cohortRes.data) {
        const refreshed = cohortRes.data;
        setCohort(refreshed);
        const latest = sortWeekEntriesDesc(refreshed?.entries)[0];
        if (refreshed.useRoster) {
          const n = (refreshed.roster || []).filter((m) => m.status === 'active').length;
          setActiveStudents(String(n));
        } else {
          setActiveStudents(latest ? String(latest.activeStudents ?? '') : '');
        }
        setLessonNumber('');
      }
      if (trendsRes.ok && trendsRes.data) {
        setDropTrends({ byLesson: trendsRes.data.byLesson || [], totalDrops: trendsRes.data.totalDrops || 0 });
      }
      setEntryDate('');
      setDroppedThisWeek('0');
      setDroppedAtLesson('');
      setNewThisWeek('0');
      setLessonNumber('');
    } catch (err) {
      setError('Failed to add entry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editForm.leader1?.trim() || !editForm.leader2?.trim()) {
      setError('Leader 1 and Leader 2 are required.');
      return;
    }
    if (editForm.useRoster && (!editForm.meetingDay?.trim() || !editForm.meetingTimeStart?.trim() || !editForm.meetingTimeEnd?.trim())) {
      setError('Meeting day and time are required when using roster.');
      return;
    }
    setError('');
    const { ok } = await patch(`/api/step-study/cohorts/${id}`, {
      classType: editForm.classType,
      startDate: editForm.startDate,
      expectedGraduationDate: editForm.expectedGraduationDate,
      leader1: editForm.leader1.trim(),
      leader2: editForm.leader2.trim(),
      leader3: editForm.leader3.trim(),
      meetingDay: (editForm.meetingDay || '').trim(),
      meetingTimeStart: (editForm.meetingTimeStart || '').trim(),
      meetingTimeEnd: (editForm.meetingTimeEnd || '').trim(),
      useRoster: !!editForm.useRoster,
    });
    if (ok) {
      const { ok: ok2, data } = await get(`/api/step-study/cohorts/${id}`);
      if (ok2) {
        setCohort(data);
        setEditForm({ ...editForm, classType: data.classType, leader1: data.leader1, leader2: data.leader2, leader3: data.leader3, meetingDay: data.meetingDay || '', meetingTimeStart: data.meetingTimeStart || '', meetingTimeEnd: data.meetingTimeEnd || '', useRoster: !!data.useRoster });
        setIsEditing(false);
      }
    }
  };

  const startEditEntry = (e) => {
    setEditingEntryId(e._id);
    setEntryEditForm({
      entryDate: e.entryDate ? new Date(e.entryDate).toISOString().split('T')[0] : '',
      activeStudents: e.activeStudents ?? 0,
      droppedThisWeek: e.droppedThisWeek ?? 0,
      droppedAtLesson: e.droppedAtLesson ?? '',
      newThisWeek: e.newThisWeek ?? 0,
      lessonNumber: e.lessonNumber ?? 1,
    });
  };

  const handleEntryEditSave = async (ev) => {
    ev.preventDefault();
    const payload = {
      ...entryEditForm,
      activeStudents: Number(entryEditForm.activeStudents) || 0,
      droppedThisWeek: Number(entryEditForm.droppedThisWeek) || 0,
      newThisWeek: Number(entryEditForm.newThisWeek) || 0,
      lessonNumber: Number(entryEditForm.lessonNumber) || 1,
    };
    if (entryEditForm.droppedAtLesson != null && entryEditForm.droppedAtLesson !== '') {
      payload.droppedAtLesson = Number(entryEditForm.droppedAtLesson);
    }
    const { ok } = await patch(`/api/step-study/cohorts/${id}/weeks/${editingEntryId}`, payload);
    if (ok) {
      const [cohortRes, trendsRes] = await Promise.all([
        get(`/api/step-study/cohorts/${id}`),
        get(`/api/step-study/cohorts/${id}/drop-trends`),
      ]);
      if (cohortRes.ok && cohortRes.data) setCohort(cohortRes.data);
      if (trendsRes.ok && trendsRes.data) {
        setDropTrends({ byLesson: trendsRes.data.byLesson || [], totalDrops: trendsRes.data.totalDrops || 0 });
      }
      setEditingEntryId(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this cohort and all its weekly entries? This cannot be undone.')) return;
    const { ok } = await del(`/api/step-study/cohorts/${id}`);
    if (ok) navigate('/step-study');
  };

  const refreshCohort = async () => {
    const [cohortRes, trendsRes] = await Promise.all([
      get(`/api/step-study/cohorts/${id}`),
      get(`/api/step-study/cohorts/${id}/drop-trends`),
    ]);
    if (cohortRes.ok && cohortRes.data) setCohort(cohortRes.data);
    if (trendsRes.ok && trendsRes.data) {
      setDropTrends({ byLesson: trendsRes.data.byLesson || [], totalDrops: trendsRes.data.totalDrops || 0 });
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError('');
    if (!memberForm.firstName?.trim() || !memberForm.lastName?.trim()) {
      setError('First name and last name are required.');
      return;
    }
    const { ok, status, data: res } = await post(`/api/step-study/cohorts/${id}/roster`, {
      firstName: memberForm.firstName.trim(),
      lastName: memberForm.lastName.trim(),
      cellphone: formatUsLocalPhone(memberForm.cellphone || ''),
      email: (memberForm.email || '').trim(),
      okTexts: memberForm.okTexts !== false,
      okEmails: memberForm.okEmails !== false,
      isLeader: !!memberForm.isLeader,
    });
    if (!ok) {
      setError(getErrorMessage({ status, data: res }, 'Failed to add member.'));
      return;
    }
    setShowAddMember(false);
    setMemberForm({ firstName: '', lastName: '', cellphone: '', email: '', okTexts: true, okEmails: true, isLeader: false });
    await refreshCohort();
  };

  const startEditMember = (m) => {
    setEditingMemberId(m._id);
    setMemberForm({
      firstName: m.firstName || '',
      lastName: m.lastName || '',
      cellphone: formatUsLocalPhone(m.cellphone || ''),
      email: m.email || '',
      okTexts: m.okTexts !== false,
      okEmails: m.okEmails !== false,
      isLeader: !!m.isLeader,
    });
  };

  const handleEditMemberSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!memberForm.firstName?.trim() || !memberForm.lastName?.trim()) {
      setError('First name and last name are required.');
      return;
    }
    const { ok, status, data: res } = await patch(`/api/step-study/cohorts/${id}/roster/${editingMemberId}`, {
      firstName: memberForm.firstName.trim(),
      lastName: memberForm.lastName.trim(),
      cellphone: formatUsLocalPhone(memberForm.cellphone || ''),
      email: (memberForm.email || '').trim(),
      okTexts: memberForm.okTexts !== false,
      okEmails: memberForm.okEmails !== false,
      isLeader: !!memberForm.isLeader,
    });
    if (!ok) {
      setError(getErrorMessage({ status, data: res }, 'Failed to update member.'));
      return;
    }
    setEditingMemberId(null);
    setMemberForm({ firstName: '', lastName: '', cellphone: '', email: '', okTexts: true, okEmails: true, isLeader: false });
    await refreshCohort();
  };

  const handleMarkDropped = async (m) => {
    if (!window.confirm(`Mark ${m.firstName} ${m.lastName} as dropped?`)) return;
    const { ok } = await patch(`/api/step-study/cohorts/${id}/roster/${m._id}`, { status: 'dropped' });
    if (ok) await refreshCohort();
  };

  const entries = useMemo(() => sortWeekEntriesDesc(cohort?.entries ?? []), [cohort?.entries]);

  /**
   * 12 rolling class weeks (this meeting + 11 prior), newest first.
   * Slot key = actual meeting calendar date (cohort meeting day), not Monday “week of”.
   */
  const attendanceWeekSlots = useMemo(() => {
    const meetingDayIndex = meetingDayIndexFromLabel(cohort?.meetingDay);
    const entryByMeeting = new Map();
    for (const e of entries) {
      const t = new Date(e.entryDate).getTime();
      if (Number.isNaN(t)) continue;
      const canon = canonicalMeetingDateForEntryDate(new Date(e.entryDate), meetingDayIndex);
      const wk = canon.getTime();
      const prev = entryByMeeting.get(wk);
      if (!prev) {
        entryByMeeting.set(wk, e);
        continue;
      }
      const prevCa = prev.createdAt ? new Date(prev.createdAt).getTime() : 0;
      const newCa = e.createdAt ? new Date(e.createdAt).getTime() : 0;
      if (newCa > prevCa || (newCa === prevCa && String(e._id) > String(prev._id))) {
        entryByMeeting.set(wk, e);
      }
    }
    const thisMonday = startOfWeekMonday(new Date());
    const slots = [];
    for (let i = 0; i < 12; i++) {
      const weekStart = new Date(thisMonday);
      weekStart.setDate(thisMonday.getDate() - i * 7);
      const meetingDate = meetingDateForMondayWeek(weekStart, meetingDayIndex);
      const wk = meetingDate.getTime();
      slots.push({
        weekStart,
        meetingDate,
        weekKey: wk,
        entry: entryByMeeting.get(wk) ?? null,
      });
    }
    return slots;
  }, [entries, cohort?.meetingDay]);

  const firstAttendanceEntryId = attendanceWeekSlots.find((s) => s.entry)?.entry?._id;

  useEffect(() => {
    if (!attendanceWeekSlots.length) return;
    setAttendanceSelection((prev) => {
      if (prev?.type === 'entry') {
        const still = attendanceWeekSlots.some(
          (s) => s.entry && String(s.entry._id) === String(prev.id)
        );
        if (still) return prev;
      }
      if (prev?.type === 'week') {
        const slot = attendanceWeekSlots.find((s) => s.weekKey === prev.weekKey);
        if (slot) {
          if (slot.entry) return { type: 'entry', id: String(slot.entry._id) };
          return prev;
        }
      }
      const firstWith = attendanceWeekSlots.find((s) => s.entry);
      if (firstWith?.entry) return { type: 'entry', id: String(firstWith.entry._id) };
      return { type: 'week', weekKey: attendanceWeekSlots[0].weekKey };
    });
  }, [attendanceWeekSlots]);

  const selectedEntryId =
    attendanceSelection?.type === 'entry' ? attendanceSelection.id : null;

  const selectedEntry = selectedEntryId
    ? entries.find((e) => String(e._id) === String(selectedEntryId))
    : null;

  const attendanceDraftKey =
    attendanceSelection?.type === 'entry'
      ? String(attendanceSelection.id)
      : attendanceSelection?.type === 'week'
        ? `w:${attendanceSelection.weekKey}`
        : '';

  useEffect(() => {
    if (attendanceSelection?.type === 'entry' && selectedEntry?.lessonNumber != null) {
      setAttendanceLessonInput(String(selectedEntry.lessonNumber));
    }
  }, [attendanceSelection, selectedEntry?.lessonNumber]);
  const getAttendanceForEntry = (entry) => {
    const map = {};
    (entry?.attendance || []).forEach((a) => {
      map[String(a.member)] = attendanceRecordStatusFromApi(a);
    });
    return map;
  };
  const attendanceForSelected =
    attendanceDraftKey && (selectedEntry || attendanceSelection?.type === 'week')
      ? attendanceDraft[attendanceDraftKey] !== undefined
        ? attendanceDraft[attendanceDraftKey]
        : getAttendanceForEntry(selectedEntry)
      : {};

  const handleAttendanceChange = (memberId, status) => {
    if (!attendanceDraftKey) return;
    setAttendanceDraft((prev) => ({
      ...prev,
      [attendanceDraftKey]: {
        ...(prev[attendanceDraftKey] ?? getAttendanceForEntry(selectedEntry)),
        [String(memberId)]: status,
      },
    }));
  };

  const handleSaveAttendance = async () => {
    if (!cohort?.roster?.length || !attendanceSelection) return;
    setIsSavingAttendance(true);
    setAttendanceSaveError('');
    const lessonNum = Math.max(1, Number(attendanceLessonInput) || 1);

    const draftKeyForPatch =
      attendanceSelection.type === 'entry'
        ? String(attendanceSelection.id)
        : `w:${attendanceSelection.weekKey}`;
    const attMapSnapshot = {
      ...(attendanceDraft[draftKeyForPatch] ??
        (attendanceSelection.type === 'entry' ? getAttendanceForEntry(selectedEntry) : {})),
    };

    let workingEntryId = attendanceSelection.type === 'entry' ? attendanceSelection.id : null;

    if (attendanceSelection.type === 'week') {
      const slot = attendanceWeekSlots.find((s) => s.weekKey === attendanceSelection.weekKey);
      if (!slot) {
        setIsSavingAttendance(false);
        return;
      }
      const wd = slot.meetingDate;
      const entryDateStr = `${wd.getFullYear()}-${String(wd.getMonth() + 1).padStart(2, '0')}-${String(wd.getDate()).padStart(2, '0')}`;
      const { ok, status, data: res } = await post(`/api/step-study/cohorts/${id}/weeks`, {
        entryDate: entryDateStr,
        droppedThisWeek: 0,
        newThisWeek: 0,
        lessonNumber: lessonNum,
      });
      if (!ok || !res?._id) {
        setAttendanceSaveError(getErrorMessage({ status, data: res }, 'Could not create the week. Try again or use Add Weekly Entry.'));
        setIsSavingAttendance(false);
        return;
      }
      workingEntryId = String(res._id);
      const wKey = `w:${attendanceSelection.weekKey}`;
      setAttendanceDraft((prev) => {
        const next = { ...prev };
        if (next[wKey]) {
          next[workingEntryId] = next[wKey];
          delete next[wKey];
        }
        return next;
      });
      setAttendanceSelection({ type: 'entry', id: workingEntryId });
      await refreshCohort();
    }

    if (!workingEntryId) {
      setIsSavingAttendance(false);
      return;
    }

    const lessonRes = await patch(`/api/step-study/cohorts/${id}/weeks/${workingEntryId}`, { lessonNumber: lessonNum });
    if (!lessonRes.ok) {
      setIsSavingAttendance(false);
      return;
    }
    const attendance = cohort.roster
      .filter((m) => m.status === 'active')
      .map((m) => ({
        memberId: m._id,
        status: attMapSnapshot[String(m._id)] || 'absent',
      }));
    const { ok } = await patch(`/api/step-study/cohorts/${id}/weeks/${workingEntryId}/attendance`, { attendance });
    if (ok) {
      setAttendanceDraft((prev) => {
        const next = { ...prev };
        delete next[workingEntryId];
        return next;
      });
      await refreshCohort();
    }
    setIsSavingAttendance(false);
  };

  const handlePrintRoster = async () => {
    const entryId =
      (attendanceSelection?.type === 'entry' && attendanceSelection.id) ||
      firstAttendanceEntryId ||
      entries[0]?._id;
    const url = entryId ? `/api/step-study/cohorts/${id}/roster/print?entryId=${entryId}` : `/api/step-study/cohorts/${id}/roster/print`;
    const { ok, data } = await get(url);
    if (!ok || !data) return;
    const displayDate = data.nextMeetingDate ? formatDate(data.nextMeetingDate) : (data.meetingDate ? formatDate(data.meetingDate) : '—');
    const timeInfo = data.meetingTime ? ` • ${data.meetingTime}` : '';
    const footerNote = escapeHtml(buildRosterSignInFooter(data, displayDate, timeInfo));
    const win = window.open('', '_blank');
    win.document.write(`
      <!DOCTYPE html><html><head><meta charset="utf-8"/><title>Roster - ${escapeHtml(data.cohortName)}</title>
      <style>
      *{box-sizing:border-box;}
      body{
        font-family:system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
        font-size:11px;
        color:#000;
        line-height:1.35;
        margin:0;
        padding:14px 16px 24px;
        max-width:720px;
      }
      .roster-header{
        border-bottom:2px solid #000;
        padding-bottom:10px;
        margin-bottom:12px;
      }
      h1{
        font-size:1.3rem;
        font-weight:700;
        margin:0 0 6px 0;
        color:#000;
        letter-spacing:-0.02em;
      }
      .roster-meta{margin:0;font-size:0.95rem;color:#222;}
      .roster-meta strong{color:#000;}
      table{
        width:100%;
        border-collapse:collapse;
        table-layout:fixed;
        font-size:10.5px;
      }
      col.col-last{width:12%;}
      col.col-first{width:12%;}
      col.col-phone{width:22%;}
      col.col-email{width:35%;}
      col.col-attended{width:19%;}
      .col-phone,.roster-table td:nth-child(3){
        white-space:nowrap;
        font-variant-numeric:tabular-nums;
        letter-spacing:0.02em;
      }
      th,td{
        border:1px solid #000;
        padding:6px 8px;
        text-align:left;
        vertical-align:middle;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
      thead th{
        background:#e0e0e0;
        color:#000;
        font-weight:700;
        font-size:10px;
        text-transform:uppercase;
        letter-spacing:0.04em;
        -webkit-print-color-adjust:exact;
        print-color-adjust:exact;
      }
      tbody td{text-overflow:ellipsis;}
      tbody tr:nth-child(odd){background:#fff;}
      tbody tr:nth-child(even){background:#f2f2f2;}
      .col-attended{text-align:center;}
      .roster-foot{
        margin-top:12px;
        font-size:10px;
        color:#222;
        line-height:1.45;
        max-width:42rem;
      }
      @page{
        size:A4 portrait;
        margin:12mm;
      }
      @media print{
        body{padding:0;max-width:none;}
        .roster-header{border-bottom-color:#000;}
        thead th,tbody td{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      }
      </style></head><body>
      <div class="roster-header">
        <h1>${escapeHtml(data.cohortName)}</h1>
        <p class="roster-meta"><strong>Next meeting:</strong> ${escapeHtml(displayDate)}${escapeHtml(timeInfo)}</p>
      </div>
      <table class="roster-table">
        <colgroup>
          <col class="col-last"/><col class="col-first"/><col class="col-phone"/><col class="col-email"/><col class="col-attended"/>
        </colgroup>
        <thead><tr>
          <th>Last name</th><th>First name</th><th>Cell</th><th>Email</th><th>Attended?</th>
        </tr></thead>
        <tbody>
      ${(data.rows || [])
        .map(
          (r) =>
            `<tr><td title="${escapeHtml(r.lastName)}">${escapeHtml(truncateOneLine(r.lastName, 20))}</td><td title="${escapeHtml(r.firstName)}">${escapeHtml(truncateOneLine(r.firstName, 20))}</td><td class="col-phone" title="${escapeHtml(String(r.cellphone || ''))}">${escapeHtml(truncateOneLine(r.cellphone || '', 28))}</td><td title="${escapeHtml(String(r.email || ''))}">${escapeHtml(truncateOneLine(r.email || '', 42))}</td><td></td></tr>`
        )
        .join('')}
        </tbody>
      </table>
      <p class="roster-foot">${footerNote}</p>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 100);
  };

  const handleDownloadRoster = async () => {
    const entryId =
      (attendanceSelection?.type === 'entry' && attendanceSelection.id) ||
      firstAttendanceEntryId ||
      entries[0]?._id;
    const url = entryId ? `/api/step-study/cohorts/${id}/roster/print?entryId=${entryId}` : `/api/step-study/cohorts/${id}/roster/print`;
    const { ok, data } = await get(url);
    if (!ok || !data) return;
    const rows = data.rows || [];
    const displayDate = data.nextMeetingDate ? formatDate(data.nextMeetingDate) : (data.meetingDate ? formatDate(data.meetingDate) : '—');
    const timeInfo = data.meetingTime ? ` • ${data.meetingTime}` : '';
    const footerNote = buildRosterSignInFooter(data, displayDate, timeInfo);

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 14;
    const pageW = doc.internal.pageSize.getWidth();
    const innerW = pageW - margin * 2;
    /* Explicit widths must sum to innerW — avoids jspdf-autotable adding a stray column. */
    const colW = {
      last: 24,
      first: 24,
      cell: 34,
      attended: 20,
      email: 0,
    };
    colW.email = innerW - colW.last - colW.first - colW.cell - colW.attended;
    const tableW = colW.last + colW.first + colW.cell + colW.email + colW.attended;

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(data.cohortName || 'Roster', margin, 18);
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text(`Next meeting: ${displayDate}${timeInfo}`, margin, 25);

    autoTable(doc, {
      startY: 30,
      margin: { left: margin, right: margin },
      tableWidth: tableW,
      head: [['Last name', 'First name', 'Cell', 'Email', 'Attended?']],
      body: rows.map((r) => [
        truncateOneLine(r.lastName, 22),
        truncateOneLine(r.firstName, 22),
        truncateOneLine(r.cellphone || '', 26),
        truncateOneLine(r.email || '', 50),
        '',
      ]),
      theme: 'grid',
      styles: {
        fontSize: 8.5,
        cellPadding: 2.5,
        valign: 'middle',
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        minCellHeight: 7.5,
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [224, 224, 224],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8.5,
        halign: 'left',
      },
      columnStyles: {
        0: { cellWidth: colW.last, halign: 'left' },
        1: { cellWidth: colW.first, halign: 'left' },
        2: { cellWidth: colW.cell, halign: 'left' },
        3: { cellWidth: colW.email, halign: 'left' },
        4: { cellWidth: colW.attended, halign: 'center' },
      },
      bodyStyles: { fillColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [242, 242, 242] },
    });

    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 28;
    doc.setFontSize(8.5);
    doc.setTextColor(75, 85, 99);
    const footerLines = doc.splitTextToSize(footerNote, pageW - margin * 2);
    doc.text(footerLines, margin, finalY + 9);

    const dateForFile = data.nextMeetingDate || data.meetingDate;
    doc.save(`roster-${(data.cohortName || 'cohort').replace(/[^a-z0-9]/gi, '-')}-${dateForFile ? new Date(dateForFile).toISOString().split('T')[0] : 'export'}.pdf`);
  };

  if (!cohort) return <div className="step-study-page"><p>Loading…</p></div>;

  const { stats } = cohort;
  const isActive = cohort.status === 'active';

  const displayName = cohort.displayName || `${cohort.classType}${(cohort.leader1 || cohort.leader2) ? ` - ${[cohort.leader1, cohort.leader2, cohort.leader3].filter(Boolean).join(', ')}` : ''}`;

  const origStart = cohort.startDate ? new Date(cohort.startDate).toISOString().split('T')[0] : '';
  const origGrad = cohort.expectedGraduationDate ? new Date(cohort.expectedGraduationDate).toISOString().split('T')[0] : '';
  const hasEdits = isEditing && (
    editForm.classType !== cohort.classType ||
    editForm.startDate !== origStart ||
    editForm.expectedGraduationDate !== origGrad ||
    (editForm.leader1 || '').trim() !== (cohort.leader1 || '') ||
    (editForm.leader2 || '').trim() !== (cohort.leader2 || '') ||
    (editForm.leader3 || '').trim() !== (cohort.leader3 || '') ||
    (editForm.meetingDay || '').trim() !== (cohort.meetingDay || '') ||
    (editForm.meetingTimeStart || '').trim() !== (cohort.meetingTimeStart || '') ||
    (editForm.meetingTimeEnd || '').trim() !== (cohort.meetingTimeEnd || '') ||
    !!editForm.useRoster !== !!cohort.useRoster
  );

  return (
    <div className="step-study-page">
      <PageHeader title={displayName} backTo="/step-study" backLabel="← Dashboard">
        <p className="step-study-subtitle" style={{ marginTop: '0.25rem' }}>
          Started {formatDate(cohort.startDate)} • Graduation: {formatDate(cohort.expectedGraduationDate)}
          {cohort.meetingDay && (
            <> • Meets {cohort.meetingDay}{cohort.meetingTimeStart && cohort.meetingTimeEnd ? ` ${cohort.meetingTimeStart} - ${cohort.meetingTimeEnd}` : (cohort.meetingTime ? ` ${cohort.meetingTime}` : '')}</>
          )}
        </p>
      </PageHeader>

      <div className="step-study-dashboard-instructions-wrap">
        <button
          type="button"
          id="step-study-cohort-instructions-toggle"
          aria-expanded={cohortPageInstructionsOpen}
          aria-controls="step-study-cohort-instructions"
          className="step-study-dashboard-instructions-btn"
          onClick={() => setCohortPageInstructionsOpen((o) => !o)}
        >
          <span aria-hidden>{cohortPageInstructionsOpen ? '▼' : '▶'}</span>
          <strong>How this cohort page works</strong>
          <span className="step-study-dashboard-instructions-hint">
            {cohortPageInstructionsOpen ? '(hide)' : '(tap to expand)'}
          </span>
        </button>
        {cohortPageInstructionsOpen && (
          <div
            id="step-study-cohort-instructions"
            className="step-study-instructions step-study-dashboard-instructions-body step-study-cohort-instructions-body"
          >
            <p className="step-study-instructions-lead">
              This is the home page for one Step Study class (one cohort). Everything below helps you track people, weekly numbers, and trends. Expand each section (▶) as you need it.
            </p>
            <ol>
              <li>
                <strong>Two ways to run a cohort (pick one per class):</strong> <strong>(1) Roster on</strong> — Use <strong>Roster</strong> + <strong>Attendance</strong> for names, lesson, and per-person attendance. <strong>Add Weekly Entry</strong> is optional (e.g. drops/new counts). <strong>(2) Roster off</strong> — No name list; use <strong>Add Weekly Entry</strong> each week for date, lesson, and class totals only. Switch under <strong>Edit Cohort</strong> if needed.
              </li>
              <li>
                <strong>Summary numbers</strong> — Flow-through, Active, Dropped, Graduated, and Lesson give a quick snapshot of how this class is doing.
              </li>
              <li>
                <strong>Edit Cohort</strong> — Change class type, start/graduation dates, leaders, and meeting day/time. Use roster = names + attendance; leave it off = weekly manual totals only. Save when done.
              </li>
              {isActive && (
                <li>
                  <strong>Mark as Graduated</strong> — When the class finishes, use this to move it to the &quot;Graduated&quot; list on the dashboard. You&apos;ll be asked to confirm. <strong>Delete</strong> removes this cohort and all its data permanently—only if you&apos;re sure.
                </li>
              )}
              {!isActive && (
                <li>
                  <strong>This cohort is graduated.</strong> You can still review history and charts. <strong>Edit Cohort</strong> updates display info; <strong>Delete</strong> removes all records for this class.
                </li>
              )}
              {isActive && cohort.useRoster && (
                <li>
                  <strong>Attendance alerts</strong> — One list flags <strong>three missed meetings in a row</strong> (absent or excused; excused counts like absent). Another flags active members whose <strong>attendance is under 75%</strong> (based on Present meetings recorded). Tap <strong>Acknowledge</strong> on each name after you&apos;ve reached out.
                </li>
              )}
              {isActive && cohort.useRoster && (
                <li>
                  <strong>Roster</strong> — Add each person with <strong>+ Add Member</strong>. Edit contact info or mark <strong>Drop</strong> when someone leaves. The table shows times present, attendance percentage (color-coded), and more.
                </li>
              )}
              {isActive && cohort.useRoster && (
                <li>
                  <strong>Attendance</strong> — Pick the meeting date (last 12 meetings on your cohort&apos;s day from <strong>Edit Cohort</strong>), set lesson, mark each person, <strong>Save Attendance</strong> (this can create the week row if it doesn&apos;t exist yet). <strong>Print Roster</strong> / <strong>Download PDF</strong> use the meeting you selected. Saved weeks show as <strong>Completed</strong> in the menu.
                </li>
              )}
              {isActive && !cohort.useRoster && (
                <li>
                  <strong>No roster</strong> — This cohort does not use the Attendance section. Enter each week&apos;s <strong>date</strong>, <strong>lesson</strong>, and <strong>totals</strong> only in <strong>Add Weekly Entry</strong>. Use <strong>Edit Cohort</strong> to turn on a roster later if you want per-person attendance.
                </li>
              )}
              {isActive && cohort.useRoster && (
                <li>
                  <strong>Add Weekly Entry (optional)</strong> — With a roster, you usually don&apos;t need this for attendance (use <strong>Attendance</strong> above). Open it when you want to add a week or adjust <strong>dropped / new</strong> counts without going through Attendance.
                </li>
              )}
              {isActive && !cohort.useRoster && (
                <li>
                  <strong>Add Weekly Entry (required)</strong> — Each week: <strong>week-of date</strong>, <strong>lesson</strong>, active/dropped/new totals. If people dropped, note which <strong>lesson</strong>. Then <strong>Save Weekly Entry</strong>.
                </li>
              )}
              {entries.length > 0 && (
                <li>
                  <strong>Weekly History</strong> — Lists every week you&apos;ve saved. Use <strong>Edit</strong> on a row to fix a mistake.
                </li>
              )}
              {entries.length > 0 && (
                <li>
                  <strong>Active Participants — Week by Week</strong> — Chart of active count over time. <strong>Drop Trends</strong> shows how many dropped at each lesson number (helps spot patterns).
                </li>
              )}
              {!isActive && cohort.useRoster && cohort.rosterStatsSnapshot && (
                <li>
                  <strong>Roster Stats at Graduation</strong> — Snapshot of active/dropped/meeting counts when the class finished.
                </li>
              )}
            </ol>
          </div>
        )}
      </div>

      {isEditing && (
        <form onSubmit={handleEditSave} className="step-study-edit-form">
          <h3>Edit Cohort</h3>
          <div className="form-group">
            <label>Class Type</label>
            <select value={editForm.classType} onChange={(e) => setEditForm({ ...editForm, classType: e.target.value })}>
              {CLASS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Start Date</label>
            <input type="date" value={editForm.startDate} onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Expected Graduation Date</label>
            <input type="date" value={editForm.expectedGraduationDate} onChange={(e) => setEditForm({ ...editForm, expectedGraduationDate: e.target.value })} required />
          </div>
          <div className="form-group">
            <label>Leader 1</label>
            <input type="text" value={editForm.leader1} onChange={(e) => setEditForm({ ...editForm, leader1: e.target.value })} placeholder="e.g., John D." required />
          </div>
          <div className="form-group">
            <label>Leader 2</label>
            <input type="text" value={editForm.leader2} onChange={(e) => setEditForm({ ...editForm, leader2: e.target.value })} placeholder="e.g., Jane S." required />
          </div>
          <div className="form-group">
            <label>Leader 3 (optional)</label>
            <input type="text" value={editForm.leader3} onChange={(e) => setEditForm({ ...editForm, leader3: e.target.value })} placeholder="e.g., Mike T." />
          </div>
          <div className="form-group step-study-roster-checkboxes">
            <label>
              <input type="checkbox" checked={!!editForm.useRoster} onChange={(e) => setEditForm({ ...editForm, useRoster: e.target.checked })} />
              Use roster (track members, attendance, print roster)
            </label>
          </div>
          {editForm.useRoster && (
            <>
              <div className="form-group">
                <label>Meeting Day</label>
                <select value={editForm.meetingDay} onChange={(e) => setEditForm({ ...editForm, meetingDay: e.target.value })} required>
                  <option value="">Select day</option>
                  {MEETING_DAYS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Meeting Start Time</label>
                <select value={editForm.meetingTimeStart} onChange={(e) => setEditForm({ ...editForm, meetingTimeStart: e.target.value })} required>
                  <option value="">Select start</option>
                  {MEETING_TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Meeting End Time</label>
                <select value={editForm.meetingTimeEnd} onChange={(e) => setEditForm({ ...editForm, meetingTimeEnd: e.target.value })} required>
                  <option value="">Select end</option>
                  {MEETING_TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          {error && <p style={{ color: 'var(--cr-text)', gridColumn: '1 / -1', fontSize: '0.9rem' }}>{error}</p>}
          {hasEdits && (
            <button type="submit" className="step-study-btn-save">
              Save Changes
            </button>
          )}
        </form>
      )}

      <div className="step-study-stats-box step-study-report-stats">
        <div className="step-study-stat"><span>Flow-through</span><strong>{stats?.flowThrough ?? 0}</strong></div>
        <div className="step-study-stat"><span>Active</span><strong>{stats?.currentActive ?? 0}</strong></div>
        <div className="step-study-stat"><span>Dropped</span><strong>{stats?.totalDropped ?? 0}</strong></div>
        <div className="step-study-stat"><span>Graduated</span><strong>{stats?.totalGraduated ?? 0}</strong></div>
        <div className="step-study-stat"><span>Lesson</span><strong>{stats?.latestLesson ?? '—'}</strong></div>
      </div>
      <div className="step-study-cohort-actions">
        <button type="button" className="step-study-btn step-study-btn-edit" onClick={() => { setIsEditing(!isEditing); setError(''); }}>
          {isEditing ? 'Cancel Edit' : 'Edit Cohort'}
        </button>
        {isActive && (
          <button
            type="button"
            className="step-study-btn step-study-btn-graduate"
            onClick={async () => {
              const msg = cohort.useRoster
                ? 'Mark this cohort as graduated? Individual roster data will be removed, but aggregate stats (active, dropped, meetings) will be saved. This cannot be undone.'
                : 'Mark this cohort as graduated? This cannot be undone.';
              if (!window.confirm(msg)) return;
              const gradDate = new Date().toISOString().split('T')[0];
              const { ok } = await patch(`/api/step-study/cohorts/${id}`, {
                status: 'graduated',
                graduationDate: gradDate,
              });
              if (ok) {
                await refreshCohort();
              }
            }}
          >
            Mark as Graduated
          </button>
        )}
        <button type="button" className="step-study-btn step-study-btn-delete" onClick={handleDelete}>
          Delete
        </button>
      </div>

      {isActive && cohort.useRoster && (cohort.attendanceAlerts?.length > 0 || cohort.lowAttendanceAlerts?.length > 0) && (
        <div className="step-study-alerts-banner" style={{ marginTop: '1rem' }}>
          {cohort.attendanceAlerts?.length > 0 && (
            <>
              <p className="step-study-alert-lead">
                <strong>Attendance alert:</strong> The following members have missed 3 meetings in a row:
              </p>
              <ul className="step-study-alert-members" aria-label="Members with three missed meetings in a row">
                {cohort.attendanceAlerts.map((a) => (
                  <li key={`streak-${a._id}`} className="step-study-alert-member">
                    <span className="step-study-alert-name">
                      {a.firstName} {a.lastName}
                    </span>
                    <button
                      type="button"
                      className="step-study-alert-ack"
                      onClick={async () => {
                        const { ok } = await post(`/api/step-study/cohorts/${id}/acknowledge-alert/${a._id}`);
                        if (ok) await refreshCohort();
                      }}
                      title="Click to acknowledge"
                    >
                      Acknowledge
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {cohort.lowAttendanceAlerts?.length > 0 && (
            <>
              <p className={`step-study-alert-lead ${cohort.attendanceAlerts?.length > 0 ? 'step-study-alert-lead--follow' : ''}`}>
                <strong>Attendance alert:</strong> The following members have attendance under 75%:
              </p>
              <ul className="step-study-alert-members" aria-label="Members under 75 percent attendance">
                {cohort.lowAttendanceAlerts.map((a) => (
                  <li key={`low-${a._id}`} className="step-study-alert-member">
                    <span className="step-study-alert-name">
                      {a.firstName} {a.lastName}
                      {a.attendancePercent != null ? (
                        <span className="step-study-alert-pct"> ({a.attendancePercent}%)</span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="step-study-alert-ack"
                      onClick={async () => {
                        const { ok } = await post(`/api/step-study/cohorts/${id}/acknowledge-alert/${a._id}?type=lowAttendance`);
                        if (ok) await refreshCohort();
                      }}
                      title="Click to acknowledge"
                    >
                      Acknowledge
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="step-study-alert-foot">Consider reaching out.</p>
        </div>
      )}

      {isActive && !cohort.useRoster && (
        <div className="step-study-section" style={{ marginTop: '1.5rem' }}>
          <p className="step-study-subtitle">
            <strong>No roster for this cohort</strong> — There is no Attendance section. Use <strong>Add Weekly Entry</strong> each week for date, lesson, and class totals.{' '}
            <button type="button" className="step-study-instructions-toggle" onClick={() => { setIsEditing(true); setError(''); }}>
              Edit cohort to enable roster
            </button>
          </p>
        </div>
      )}

      {isActive && cohort.useRoster && (
        <div className="step-study-collapsible-section" style={{ marginTop: '1rem' }}>
          <button type="button" className="step-study-collapsible-header" onClick={() => setSectionOpen((s) => ({ ...s, roster: !s.roster }))}>
            <span>{sectionOpen.roster ? '▼' : '▶'}</span> Roster
          </button>
          {sectionOpen.roster && (
          <div className="step-study-collapsible-content">
          <p className="step-study-subtitle" style={{ marginBottom: '1rem' }}>
            Active: <strong>{cohort.roster?.filter((m) => m.status === 'active').length ?? 0}</strong>
            {' • '}Dropped: <strong>{cohort.roster?.filter((m) => m.status === 'dropped').length ?? 0}</strong>
          </p>
          {!showAddMember && !editingMemberId && (
            <button type="button" className="step-study-btn step-study-btn-primary" onClick={() => { setShowAddMember(true); setError(''); }} style={{ marginBottom: '1rem' }}>
              + Add Member
            </button>
          )}
          {(showAddMember || editingMemberId) && (
            <form onSubmit={showAddMember ? handleAddMember : handleEditMemberSave} className="step-study-roster-form">
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>{editingMemberId ? 'Edit Member' : 'Add Member'}</h4>
              <div className="step-study-roster-form-grid">
                <div className="form-group">
                  <label>First Name</label>
                  <input type="text" value={memberForm.firstName} onChange={(e) => setMemberForm({ ...memberForm, firstName: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input type="text" value={memberForm.lastName} onChange={(e) => setMemberForm({ ...memberForm, lastName: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Cellphone</label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={memberForm.cellphone}
                    onChange={(e) => setMemberForm({ ...memberForm, cellphone: formatUsLocalPhone(e.target.value) })}
                    onBlur={(e) => setMemberForm((prev) => ({ ...prev, cellphone: formatUsLocalPhone(e.target.value) }))}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} placeholder="Optional" />
                </div>
                <div className="form-group step-study-roster-checkboxes">
                  <label><input type="checkbox" checked={memberForm.okTexts} onChange={(e) => setMemberForm({ ...memberForm, okTexts: e.target.checked })} /> Ok to receive texts</label>
                </div>
                <div className="form-group step-study-roster-checkboxes">
                  <label><input type="checkbox" checked={memberForm.okEmails} onChange={(e) => setMemberForm({ ...memberForm, okEmails: e.target.checked })} /> Ok to receive emails</label>
                </div>
                <div className="form-group step-study-roster-checkboxes">
                  <label><input type="checkbox" checked={memberForm.isLeader} onChange={(e) => setMemberForm({ ...memberForm, isLeader: e.target.checked })} /> Leader</label>
                </div>
              </div>
              {error && <p style={{ color: 'var(--cr-text)', fontSize: '0.9rem', gridColumn: '1 / -1' }}>{error}</p>}
              <div className="step-study-roster-form-actions">
                <button type="submit" className="step-study-btn step-study-btn-save">{editingMemberId ? 'Save' : 'Add'}</button>
                <button type="button" className="step-study-btn step-study-btn-delete" onClick={() => { setShowAddMember(false); setEditingMemberId(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          )}
          {cohort.roster?.filter((m) => m.status === 'active').length > 0 ? (
            <div className="step-study-roster-table-wrap" style={{ overflowX: 'auto', marginTop: '1rem' }}>
              <table className="step-study-roster-table">
                <thead>
                  <tr>
                    <th>Last Name</th>
                    <th>First Name</th>
                    <th>Cellphone</th>
                    <th>Email</th>
                    <th>Texts</th>
                    <th>Emails</th>
                    <th>Leader</th>
                    <th>Status</th>
                    <th>Times present</th>
                    <th>Attendance %</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cohort.roster.filter((m) => m.status === 'active').map((m) => (
                    <tr key={m._id}>
                      <td>{m.lastName}</td>
                      <td>{m.firstName}</td>
                      <td>{formatUsLocalPhone(m.cellphone) || '—'}</td>
                      <td>{m.email || '—'}</td>
                      <td>{m.okTexts !== false ? 'Yes' : 'No'}</td>
                      <td>{m.okEmails !== false ? 'Yes' : 'No'}</td>
                      <td>{m.isLeader ? 'Yes' : '—'}</td>
                      <td><span className={`step-study-status step-study-status-${m.status}`}>{m.status}</span></td>
                      <td>{m.daysPresent ?? 0}</td>
                      <td>
                        <span
                          className={`step-study-attendance-pct step-study-attendance-pct--${attendancePercentToneClass(m.attendancePercent)}`}
                          title="Share of meetings attended in person (Present only)"
                        >
                          {m.attendancePercent ?? 0}%
                        </span>
                      </td>
                      <td>
                        {m.status === 'active' && (
                          <>
                            <button type="button" className="step-study-roster-action-btn" onClick={() => startEditMember(m)}>Edit</button>
                            <button type="button" className="step-study-roster-action-btn step-study-roster-drop" onClick={() => handleMarkDropped(m)}>Drop</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="step-study-empty" style={{ marginTop: '0.5rem' }}>No roster members yet. Add members to track attendance.</p>
          )}
          </div>
          )}
        </div>
      )}

      {isActive && cohort.useRoster && cohort.roster?.length > 0 && (
        <div className="step-study-collapsible-section" style={{ marginTop: '1rem' }}>
          <button type="button" className="step-study-collapsible-header" onClick={() => setSectionOpen((s) => ({ ...s, attendance: !s.attendance }))}>
            <span>{sectionOpen.attendance ? '▼' : '▶'}</span> Attendance
          </button>
          {sectionOpen.attendance && (
          <div className="step-study-collapsible-content">
          <p className="step-study-subtitle" style={{ marginBottom: '1rem' }}>
            <strong>Roster cohorts:</strong> Set the <strong>lesson</strong> and mark each person for the meeting here. The menu lists the <strong>last 12 meeting dates</strong> (newest first), using this cohort&apos;s <strong>meeting day</strong> from <strong>Edit Cohort</strong> (e.g. Saturday). If that date isn&apos;t saved yet, <strong>Save Attendance</strong> creates the week and stores attendance—you don&apos;t have to use <strong>Add Weekly Entry</strong> first (unless you want to log drops/new there).
          </p>
          {attendanceSaveError ? (
            <p className="step-study-subtitle" style={{ marginBottom: '1rem', color: 'var(--cr-text)', fontSize: '0.9rem' }} role="alert">
              {attendanceSaveError}
            </p>
          ) : null}
          <div className="step-study-attendance-controls" style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
            <label>
              Meeting date
              {cohort.meetingDay ? ` (${cohort.meetingDay})` : ''}:{' '}
              <select
                value={
                  attendanceSelection?.type === 'entry'
                    ? `e:${attendanceSelection.id}`
                    : attendanceSelection?.type === 'week'
                      ? `w:${attendanceSelection.weekKey}`
                      : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.startsWith('e:')) setAttendanceSelection({ type: 'entry', id: v.slice(2) });
                  else if (v.startsWith('w:')) setAttendanceSelection({ type: 'week', weekKey: parseInt(v.slice(2), 10) });
                }}
              >
                {attendanceWeekSlots.map((slot) => {
                  const label = formatDate(slot.meetingDate);
                  if (slot.entry) {
                    return (
                      <option key={slot.weekKey} value={`e:${slot.entry._id}`}>
                        {label} — Completed
                      </option>
                    );
                  }
                  return (
                    <option key={slot.weekKey} value={`w:${slot.weekKey}`}>
                      {label} — save attendance to add week
                    </option>
                  );
                })}
              </select>
            </label>
            <label>
              Lesson #{' '}
              <input
                type="number"
                min={1}
                className="step-study-attendance-lesson-input"
                value={attendanceLessonInput}
                onChange={(e) => setAttendanceLessonInput(e.target.value)}
                aria-label="Lesson number for this meeting week"
              />
            </label>
            <button type="button" className="step-study-btn step-study-btn-primary" onClick={handleSaveAttendance} disabled={isSavingAttendance || !attendanceSelection}>
              {isSavingAttendance ? 'Saving…' : 'Save Attendance'}
            </button>
            <button type="button" className="step-study-btn step-study-btn-edit" onClick={handlePrintRoster}>Print Roster</button>
            <button type="button" className="step-study-btn step-study-btn-edit" onClick={handleDownloadRoster}>Download PDF</button>
          </div>
          <div className="step-study-attendance-grid">
            {cohort.roster.filter((m) => m.status === 'active').map((m) => {
              const raw = attendanceForSelected[String(m._id)];
              const current = typeof raw === 'boolean' ? (raw ? 'present' : 'absent') : (raw || 'absent');
              return (
                <div key={m._id} className="step-study-attendance-row">
                  <span className="step-study-attendance-name">{m.lastName}, {m.firstName}</span>
                  <div className="step-study-attendance-statuses" role="radiogroup" aria-label={`Attendance for ${m.firstName} ${m.lastName}`}>
                    {[
                      { value: 'present', label: 'Present' },
                      { value: 'absent', label: 'Absent' },
                      { value: 'excused', label: 'Excused' },
                    ].map(({ value, label }) => (
                      <label key={value} className="step-study-attendance-radio-label">
                        <input
                          type="radio"
                          name={`attendance-${m._id}`}
                          value={value}
                          checked={current === value}
                          onChange={() => handleAttendanceChange(m._id, value)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
          )}
        </div>
      )}

      {!isActive && cohort.useRoster && cohort.rosterStatsSnapshot && (
        <div className="step-study-collapsible-section" style={{ marginTop: '1rem' }}>
          <button type="button" className="step-study-collapsible-header" onClick={() => setSectionOpen((s) => ({ ...s, rosterStats: !s.rosterStats }))}>
            <span>{sectionOpen.rosterStats ? '▼' : '▶'}</span> Roster Stats at Graduation
          </button>
          {sectionOpen.rosterStats && (
          <div className="step-study-collapsible-content">
          <p className="step-study-subtitle">
            Active: <strong>{cohort.rosterStatsSnapshot.totalActive ?? 0}</strong>
            {' • '}Dropped: <strong>{cohort.rosterStatsSnapshot.totalDropped ?? 0}</strong>
            {' • '}Meetings: <strong>{cohort.rosterStatsSnapshot.totalMeetings ?? 0}</strong>
          </p>
          </div>
          )}
        </div>
      )}

      {isActive && (
        <>
        <div className="step-study-collapsible-section" style={{ marginTop: '1rem' }}>
          <button type="button" className="step-study-collapsible-header" onClick={() => setSectionOpen((s) => ({ ...s, addEntry: !s.addEntry }))}>
            <span>{sectionOpen.addEntry ? '▼' : '▶'}</span> Add Weekly Entry
            {cohort.useRoster ? ' (optional)' : ''}
          </button>
          {sectionOpen.addEntry && (
          <div className="step-study-collapsible-content">
          <p className="step-study-subtitle" style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
            {cohort.useRoster ? (
              <>
                <strong>Optional with a roster:</strong> Use this to add a week or adjust <strong>dropped / new</strong> counts. Per-person attendance and lesson for the meeting belong in <strong>Attendance</strong> above. <strong>Active Students</strong> matches the roster count.
              </>
            ) : (
              <>
                <strong>Primary path (no roster):</strong> Each week enter <strong>week-of date</strong>, <strong>lesson</strong>, and class totals here—this is how you record the class without name-level attendance.
              </>
            )}
          </p>
          <form onSubmit={handleSubmit} className="step-study-entry-form">
            <div className="form-group">
              <label>Entry Date (week of)</label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                onClick={(e) => e.target.showPicker?.()}
                required
              />
            </div>
            <div className="form-group">
              <label>Active Students</label>
              {cohort.useRoster ? (
                <input
                  type="number"
                  min={0}
                  readOnly
                  value={activeStudents}
                  title="With a roster, this count is the current active members on the roster."
                  aria-readonly="true"
                />
              ) : (
                <input
                  type="number"
                  min={0}
                  value={activeStudents}
                  onChange={(e) => setActiveStudents(e.target.value)}
                  required
                />
              )}
              {cohort.useRoster && (
                <p className="step-study-field-hint" style={{ margin: '0.35rem 0 0 0', fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                  Matches the roster: <strong>{(cohort.roster || []).filter((m) => m.status === 'active').length}</strong> active member
                  {(cohort.roster || []).filter((m) => m.status === 'active').length === 1 ? '' : 's'} (add or drop people under Roster).
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Dropped This Week</label>
              <input type="number" min={0} value={droppedThisWeek} onChange={(e) => setDroppedThisWeek(e.target.value)} />
            </div>
            {Number(droppedThisWeek) > 0 && (
              <div className="form-group">
                <label>Dropped at Lesson # (optional)</label>
                <input
                  type="number"
                  min={1}
                  placeholder={lessonNumber ? `Default: ${lessonNumber}` : 'e.g. 5'}
                  value={droppedAtLesson}
                  onChange={(e) => setDroppedAtLesson(e.target.value)}
                />
              </div>
            )}
            <div className="form-group">
              <label>New This Week</label>
              <input type="number" min={0} value={newThisWeek} onChange={(e) => setNewThisWeek(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Current Lesson #</label>
              <input type="number" min={1} value={lessonNumber} onChange={(e) => setLessonNumber(e.target.value)} placeholder="Enter lesson (varies by cohort)" required />
            </div>
            {error && <p style={{ color: 'var(--cr-text)', fontSize: '0.9rem' }}>{error}</p>}
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save Weekly Entry'}
            </button>
          </form>
          </div>
          )}
        </div>
        </>
      )}

      {entries?.length > 0 && (
        <div className="step-study-collapsible-section" style={{ marginTop: '1rem' }}>
          <button type="button" className="step-study-collapsible-header" onClick={() => setSectionOpen((s) => ({ ...s, weeklyHistory: !s.weeklyHistory }))}>
            <span>{sectionOpen.weeklyHistory ? '▼' : '▶'}</span> Weekly History
          </button>
          {sectionOpen.weeklyHistory && (
          <div className="step-study-collapsible-content">
          <div className="step-study-history">
            {(showAllHistory ? entries : entries.slice(0, 4)).map((e, i) => (
              <div key={e._id || i}>
                {editingEntryId === e._id ? (
                  <form onSubmit={handleEntryEditSave} className="step-study-entry-edit-form">
                    <input type="date" value={entryEditForm.entryDate} onChange={(ev) => setEntryEditForm({ ...entryEditForm, entryDate: ev.target.value })} required />
                    <input type="number" min={0} value={entryEditForm.activeStudents} onChange={(ev) => setEntryEditForm({ ...entryEditForm, activeStudents: ev.target.value })} placeholder="Active" />
                    <input type="number" min={0} value={entryEditForm.droppedThisWeek} onChange={(ev) => setEntryEditForm({ ...entryEditForm, droppedThisWeek: ev.target.value })} placeholder="Dropped" />
                    <input type="number" min={1} value={entryEditForm.droppedAtLesson || ''} onChange={(ev) => setEntryEditForm({ ...entryEditForm, droppedAtLesson: ev.target.value })} placeholder="Drop L#" style={{ width: '55px' }} title="Lesson when dropped" />
                    <input type="number" min={0} value={entryEditForm.newThisWeek} onChange={(ev) => setEntryEditForm({ ...entryEditForm, newThisWeek: ev.target.value })} placeholder="New" />
                    <input type="number" min={1} value={entryEditForm.lessonNumber} onChange={(ev) => setEntryEditForm({ ...entryEditForm, lessonNumber: ev.target.value })} placeholder="Lesson" style={{ width: '60px' }} />
                    <button type="submit" className="step-study-btn-save-small">Save</button>
                    <button type="button" onClick={() => setEditingEntryId(null)}>Cancel</button>
                  </form>
                ) : (
                  <div className="step-study-history-row">
                    <span>{formatDate(e.entryDate)}</span>
                    <span>Active: {e.activeStudents}</span>
                    <span>Dropped: {e.droppedThisWeek}{e.droppedAtLesson ? ` @ L${e.droppedAtLesson}` : ''}</span>
                    <span>New: {e.newThisWeek}</span>
                    <span>Lesson {e.lessonNumber}</span>
                    <button type="button" className="step-study-history-edit-btn" onClick={() => startEditEntry(e)}>Edit</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {entries.length > 4 && (
            <button
              type="button"
              className="step-study-history-toggle"
              onClick={() => setShowAllHistory((v) => !v)}
            >
              {showAllHistory ? 'Show less' : `View all (${entries.length} entries)`}
            </button>
          )}
          </div>
          )}
        </div>
      )}

      {entries?.length > 0 && (
        <div className="step-study-collapsible-section" style={{ marginTop: '1rem' }}>
          <button type="button" className="step-study-collapsible-header" onClick={() => setSectionOpen((s) => ({ ...s, activeChart: !s.activeChart }))}>
            <span>{sectionOpen.activeChart ? '▼' : '▶'}</span> Active Participants — Week by Week
          </button>
          {sectionOpen.activeChart && (
          <div className="step-study-collapsible-content">
          <div style={{ maxWidth: '600px', marginBottom: '1rem' }}>
            <Bar
              data={{
                labels: [...entries].reverse().map((e) => formatDate(e.entryDate)),
                datasets: [{
                  label: 'Active',
                  data: [...entries].reverse().map((e) => e.activeStudents ?? 0),
                  backgroundColor: 'rgba(21, 128, 61, 0.5)',
                  borderColor: '#15803d',
                  borderWidth: 1,
                }],
              }}
              options={{
                responsive: true,
                plugins: {
                  title: { display: false },
                  legend: { display: false },
                },
                scales: {
                  y: { beginAtZero: true, ticks: { stepSize: 1 } },
                },
              }}
            />
          </div>
          </div>
          )}
        </div>
      )}

      {dropTrends.byLesson.length > 0 && (
        <div className="step-study-collapsible-section" style={{ marginTop: '1rem' }}>
          <button type="button" className="step-study-collapsible-header" onClick={() => setSectionOpen((s) => ({ ...s, dropTrends: !s.dropTrends }))}>
            <span>{sectionOpen.dropTrends ? '▼' : '▶'}</span> Drop Trends — Drops by Lesson
          </button>
          {sectionOpen.dropTrends && (
          <div className="step-study-collapsible-content">
          <p className="step-study-subtitle" style={{ marginBottom: '1rem' }}>
            Total drops for this cohort: <strong>{dropTrends.totalDrops}</strong>
          </p>
          <div style={{ maxWidth: '600px', marginBottom: '1rem' }}>
            <Bar
              data={{
                labels: dropTrends.byLesson.map((x) => `Lesson ${x.lesson}`),
                datasets: [{
                  label: 'Drops',
                  data: dropTrends.byLesson.map((x) => x.dropped),
                  backgroundColor: 'rgba(75, 85, 99, 0.5)',
                  borderColor: '#4b5563',
                  borderWidth: 1,
                }],
              }}
              options={{
                responsive: true,
                plugins: {
                  title: { display: false },
                  legend: { display: false },
                },
                scales: {
                  y: { beginAtZero: true, ticks: { stepSize: 1 } },
                },
              }}
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {dropTrends.byLesson.map((x) => (
              <span key={x.lesson} style={{ padding: '0.25rem 0.5rem', background: 'var(--cr-bg-warm)', borderRadius: '6px', fontSize: '0.9rem', border: '1px solid var(--cr-border)' }}>
                Lesson {x.lesson}: <strong>{x.dropped}</strong>
              </span>
            ))}
          </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepStudyCohortPage;
